import React, { useState } from 'react';
import {
    Button,
    CircularProgress,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField
} from '@material-ui/core';
import FormSection from '../../../../components/atom/FormSection';
import AutocompleteSelect from '../../../../components/atom/AutocompleteSelect';
import { onlyVendorAutocomplete } from '../../../../autocomplete';
import { useFormik } from 'formik';
import { Alert } from '@material-ui/lab';

import useExcelExport from '../../../../hooks/useExcelExport';
import { EXCEL_EXPORT_VARIANTS } from '../../../../tools/excelExportSchemas';
import { EXPORT_BATCH_VARIANTS } from '../BatchListPage';

export const levelSelectOptions = {
    detailed: 'Detailed',
    summary: 'Summary'
};

const voucherBachSelectOptions = {
    include: 'Include',
    exclude: 'Exclude',
    only: 'Only'
};

const subTotalSelectOptions = {
    byVendor: 'by Vendor',
    byVendorByMonth: 'by Vendor, by Month',
    byMonth: 'by Month',
    byMonthByVendor: 'by Month, by Vendor'
};

const initialFilterValues = {
    fromDate: '',
    vendors: [],
    vouchersBatched: 'include',
    vouchersVendorPaid: 'include',
    level: 'detailed',
    subTotal: ''
};

const AccountsPayableBlock = () => {
    const [apiError, setApiError] = useState(null);
    const { isLoading, handleExport } = useExcelExport();

    const onSubmit = async data => {
        setApiError(null);
        setSubmitting(true);
        try {
            const params = {
                exportVariant: EXPORT_BATCH_VARIANTS.ACCOUNTS_PAYABLE,
                fromDate: data.fromDate,
                level: data.level,
                subTotal: data.subTotal,
                vouchersBatched: data.vouchersBatched,
                vouchersVendorPaid: data.vouchersVendorPaid
            };
            if (data.vendors.length > 0) {
                params.vendorIds = data.vendors;
            }

            let variant = EXCEL_EXPORT_VARIANTS.ACCOUNTS_PAYABLE_DETAILED;

            switch (data.subTotal) {
                case 'byVendor':
                    variant = EXCEL_EXPORT_VARIANTS.ACCOUNTS_PAYABLE_BY_VENDOR;
                    break;
                case 'byMonth':
                    variant = EXCEL_EXPORT_VARIANTS.ACCOUNTS_PAYABLE_BY_MONTH;
                    break;
                case 'byVendorByMonth':
                    variant = EXCEL_EXPORT_VARIANTS.ACCOUNTS_PAYABLE_BY_VENDOR_BY_MONTH;
                    break;
                case 'byMonthByVendor':
                    variant = EXCEL_EXPORT_VARIANTS.ACCOUNTS_PAYABLE_BY_MONTH_BY_VENDOR;
                    break;
                default:
                    variant = EXCEL_EXPORT_VARIANTS.ACCOUNTS_PAYABLE_DETAILED;
                    break;
            }

            await handleExport({
                variant,
                params
            });
        } catch (error) {
            if (error.status === 404) {
                setApiError('No batches found for the provided filters');
            } else {
                console.log('error', error);
                setApiError(error || 'An error occurred while exporting batches');
            }
        }
        setSubmitting(false);
    };

    const { values, isValid, isSubmitting, setFieldValue, handleSubmit, setSubmitting } = useFormik(
        {
            initialValues: initialFilterValues,
            onSubmit: data => onSubmit(data)
        }
    );

    const handleInputChange = (event, field) => {
        const { value } = event.target;
        setFieldValue(field, value);
        setApiError(null);
    };

    const formDisabled = isSubmitting || isLoading;

    return (
        <FormSection title="Accounts payable">
            <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            type="date"
                            label="From"
                            fullWidth
                            value={values.fromDate}
                            name="fromDate"
                            onChange={e => handleInputChange(e, 'fromDate')}
                            InputLabelProps={{
                                shrink: true
                            }}
                            disabled={formDisabled}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <AutocompleteSelect
                            initCall={onlyVendorAutocomplete.init}
                            autocompleteCall={onlyVendorAutocomplete.search}
                            value={values.vendors ?? null}
                            setValue={newValue => {
                                setFieldValue('vendors', [...newValue]);
                                setApiError(null);
                            }}
                            disableClearable
                            multiple
                            label="Vendors"
                            disabled={formDisabled}
                        >
                            <TextField label="Vendor" />
                        </AutocompleteSelect>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <InputLabel id="vouchers-batched-selects">
                                Vouchers batched prior to Date
                            </InputLabel>
                            <Select
                                fullWidth
                                labelId="vouchers-batched-selects"
                                value={values.vouchersBatched}
                                size="small"
                                label="Vouchers batched prior to Date"
                                onChange={e => {
                                    handleInputChange(e, 'vouchersBatched');
                                }}
                                disabled={formDisabled}
                            >
                                {Object.entries(voucherBachSelectOptions).map(([key, value]) => (
                                    <MenuItem key={key} value={key}>
                                        {value}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <InputLabel id="vouchers-vendor-paid-select">
                                Vouchers that are Vendor Paid
                            </InputLabel>
                            <Select
                                fullWidth
                                labelId="vouchers-vendor-paid-select"
                                value={values.vouchersVendorPaid}
                                size="small"
                                label="Vouchers that are Vendor Paid"
                                onChange={e => {
                                    handleInputChange(e, 'vouchersVendorPaid');
                                }}
                                disabled={formDisabled}
                            >
                                {Object.entries(voucherBachSelectOptions).map(([key, value]) => (
                                    <MenuItem key={key} value={key}>
                                        {value}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <InputLabel id="level-select">Level</InputLabel>
                            <Select
                                fullWidth
                                labelId="level-select"
                                value={values.level}
                                size="small"
                                label="Level"
                                onChange={e => {
                                    handleInputChange(e, 'level');
                                    if (e.target.value === 'detailed') {
                                        setFieldValue('subTotal', '');
                                    } else {
                                        setFieldValue('subTotal', 'byVendor');
                                    }
                                }}
                                disabled={formDisabled}
                            >
                                {Object.entries(levelSelectOptions).map(([key, value]) => (
                                    <MenuItem key={key} value={key}>
                                        {value}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    {/* Sub-Total Select ONLY for Summary Level */}
                    {values.level === 'summary' && (
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel id="sub-total-select">Sub-Total</InputLabel>
                                <Select
                                    fullWidth
                                    labelId="sub-total-select"
                                    value={values.subTotal}
                                    size="small"
                                    label="Sub-Total"
                                    onChange={e => {
                                        handleInputChange(e, 'subTotal');
                                    }}
                                    disabled={formDisabled}
                                >
                                    {Object.entries(subTotalSelectOptions).map(([key, value]) => (
                                        <MenuItem key={key} value={key}>
                                            {value}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    )}
                    {apiError && (
                        <Grid item xs={12}>
                            <Alert severity="error">{apiError}</Alert>
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <Button
                            fullWidth
                            type="submit"
                            size="small"
                            variant="contained"
                            color="primary"
                            disabled={!isValid || formDisabled}
                            startIcon={
                                isSubmitting || isLoading ? <CircularProgress size={20} /> : null
                            }
                        >
                            Export
                        </Button>
                    </Grid>
                </Grid>
            </form>
        </FormSection>
    );
};

export default AccountsPayableBlock;
